import type { ReactNode } from "react";
import CheckBox from "./CheckBox";
import { staffFieldControlSlot, staffFieldRoot } from "./fieldStyles";

export type CheckBoxGroupProps<T = unknown> = {
  title?: ReactNode;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  tooltip?: string;
  className?: string;
  contentClassName?: string;
  options: T[];
  value: string[];
  onChange: (selected: string[]) => void;
  getOptionValue: (option: T) => string;
  getOptionLabel: (option: T) => ReactNode;
  getOptionDisabled?: (option: T) => boolean;
  checkboxTitleFirst?: boolean;
};

/**
 * Grupo de casillas a partir de datos: obligatorio, borde de error y mensaje a nivel de grupo.
 */
export default function CheckBoxGroup<T>({
  title,
  required = false,
  error = false,
  errorMessage,
  tooltip,
  className = "",
  contentClassName = "flex flex-col gap-2",
  options,
  value,
  onChange,
  getOptionValue,
  getOptionLabel,
  getOptionDisabled,
  checkboxTitleFirst = false,
}: CheckBoxGroupProps<T>) {
  const showContentSlot = error || options.length > 0;

  return (
    <div className={`${staffFieldRoot} ${className}`}>
      {title != null && title !== "" && (
        <div title={tooltip} className="shrink-0 font-semibold text-gray-font">
          {title}
          {required && <span className="text-primary"> *</span>}
        </div>
      )}

      {showContentSlot && (
        <div className={staffFieldControlSlot}>
          <div className={`px-1 py-0.5 ${contentClassName}`}>
            {options.map((option, index) => {
              const optionValue = getOptionValue(option);
              const key = optionValue || `option-${index}`;

              return (
                <CheckBox
                  key={key}
                  label={getOptionLabel(option)}
                  checked={value.includes(optionValue)}
                  disabled={getOptionDisabled?.(option)}
                  titleFirst={checkboxTitleFirst}
                  onCheckedChange={(checked) => {
                    const next = checked
                      ? value.includes(optionValue)
                        ? value
                        : [...value, optionValue]
                      : value.filter((v) => v !== optionValue);
                    onChange(next);
                  }}
                />
              );
            })}
          </div>
        </div>
      )}

      {error && errorMessage && (
        <span className="shrink-0 text-sm text-primary">{errorMessage}</span>
      )}
    </div>
  );
}
